import React, { useState, useRef, useEffect } from 'react';
    import { useDonation } from '../context/DonationContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiDollarSign, FiCamera, FiXCircle, FiSave, FiX, FiInfo } = FiIcons;

    function ItemForm() {
      const { state, dispatch } = useDonation();
      const fileInputRef = useRef();

      const initialFormState = {
        itemName: '',
        category: '',
        photoId: '',
        conditions: { okay: 0, good: 0, great: 0, likeNew: 0 },
        isUnique: false,
        uniquePrice: 0,
        width: 11,
        height: 14,
        ppp: 1.00
      };

      const [formData, setFormData] = useState(initialFormState);

      // CRITICAL: Use state.fmvItems instead of static fmvData to include user-added items
      const validItemNames = [...new Set(state.fmvItems.map(i => i.name))].sort();

      useEffect(() => {
        if (state.editingItemId) {
          const itemToEdit = state.currentDonation.items.find(i => i.id === state.editingItemId);
          if (itemToEdit) {
            setFormData({
              itemName: itemToEdit.itemName,
              category: itemToEdit.category,
              photoId: itemToEdit.photoId || '',
              conditions: { ...itemToEdit.conditions },
              isUnique: itemToEdit.isUnique || false,
              uniquePrice: itemToEdit.uniquePrice || 0,
              width: itemToEdit.width || 11,
              height: itemToEdit.height || 14,
              ppp: itemToEdit.ppp || 1.00
            });
          }
        } else {
          setFormData(initialFormState);
        }
      }, [state.editingItemId, state.currentDonation]);

      const isPainting = formData.itemName === 'Painting';
      
      const availableCategories = state.fmvItems
        .filter(item => item.name === formData.itemName)
        .map(item => item.category);

      useEffect(() => {
        if (formData.itemName && !isPainting && !state.editingItemId) {
          if (!availableCategories.includes(formData.category)) {
            setFormData(prev => ({ ...prev, category: availableCategories[0] || '' }));
          }
        }
      }, [formData.itemName, availableCategories.length, state.editingItemId]);

      const fmvItem = state.fmvItems.find(f => f.name === formData.itemName && f.category === formData.category);

      const getCalculatedPrices = () => {
        if (formData.isUnique) {
          const up = parseFloat(formData.uniquePrice) || 0;
          return {
            likeNew: up,
            great: Math.round(up * 0.95 * 100) / 100,
            good: Math.round(up * 0.85 * 100) / 100,
            okay: Math.round(up * 0.70 * 100) / 100
          };
        }

        if (isPainting) {
          const baseValue = formData.width * formData.height * formData.ppp;
          return {
            likeNew: Math.round(baseValue * 100) / 100,
            great: Math.round(baseValue * 0.95 * 100) / 100,
            good: Math.round(baseValue * 0.85 * 100) / 100,
            okay: Math.round(baseValue * 0.70 * 100) / 100
          };
        }

        if (!fmvItem) return null;
        const likeNewPrice = Math.round(fmvItem.high * 0.95 * 100) / 100;
        return {
          likeNew: likeNewPrice,
          great: Math.round(likeNewPrice * 0.85 * 100) / 100,
          good: Math.round(likeNewPrice * 0.70 * 100) / 100,
          okay: Math.round(likeNewPrice * 0.50 * 100) / 100
        };
      };

      const prices = getCalculatedPrices();
      const totalQuantity = Object.values(formData.conditions).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
      
      const calculateSubtotal = () => {
        if (!prices) return 0;
        return Math.round((
          (formData.conditions.likeNew || 0) * prices.likeNew +
          (formData.conditions.great || 0) * prices.great +
          (formData.conditions.good || 0) * prices.good +
          (formData.conditions.okay || 0) * prices.okay
        ) * 100) / 100;
      };

      const currentSubtotal = calculateSubtotal();
      const existingTotal = state.currentDonation?.total || 0;
      const currentItemOldSubtotal = state.editingItemId 
        ? state.currentDonation.items.find(i => i.id === state.editingItemId)?.subtotal || 0 
        : 0;
      const wouldExceedLimit = (existingTotal - currentItemOldSubtotal) + currentSubtotal > 499;

      const handlePhotoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
          const url = URL.createObjectURL(file);
          const nextId = state.photos.length + 1;
          dispatch({ type: 'ADD_PHOTO', payload: { file, url } });
          setFormData(prev => ({ ...prev, photoId: nextId }));
        }
      };

      const handleSubmit = (e) => {
        e.preventDefault();
        if (totalQuantity === 0 || wouldExceedLimit) return;

        const submissionData = { ...formData };
        if (isPainting && !formData.isUnique) {
          submissionData.category = `${formData.width}x${formData.height}`;
        }

        if (state.editingItemId) {
          dispatch({
            type: 'UPDATE_ITEM',
            payload: {
              id: state.editingItemId,
              updates: { ...submissionData, totalQuantity, subtotal: currentSubtotal, fmvCalculations: prices }
            }
          });
        } else {
          dispatch({
            type: 'ADD_ITEM',
            payload: { ...submissionData, totalQuantity, subtotal: currentSubtotal, fmvCalculations: prices }
          });
        }
        setFormData(initialFormState);
      };

      const handleCancelEdit = () => {
        dispatch({ type: 'SET_EDITING_ITEM', payload: null });
      };

      const conditionConfigs = [
        { key: 'likeNew', label: formData.isUnique ? 'New' : (isPainting ? 'New' : 'Like-New') },
        { key: 'great', label: formData.isUnique ? 'Like-New' : (isPainting ? 'Like-New' : 'Great') },
        { key: 'good', label: formData.isUnique ? 'Great' : (isPainting ? 'Great' : 'Good') },
        { key: 'okay', label: formData.isUnique ? 'Good' : (isPainting ? 'Good' : 'Okay') }
      ];

      return (
        <form onSubmit={handleSubmit} className={`bg-white border rounded-2xl shadow-sm p-6 space-y-6 transition-all ${state.editingItemId ? 'border-blue-400 ring-4 ring-blue-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-bold text-gray-900">
                {state.editingItemId ? 'Edit Item' : 'Add New Item'}
              </h3>
              {state.editingItemId && (
                <button 
                  type="button" 
                  onClick={handleCancelEdit}
                  className="text-xs font-bold text-red-500 hover:text-red-600 flex items-center bg-red-50 px-2 py-1 rounded"
                >
                  <SafeIcon icon={FiX} className="mr-1" />
                  Cancel
                </button>
              )}
            </div>
            <div className="flex flex-col items-end">
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${wouldExceedLimit ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                Subtotal: ${currentSubtotal.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Item Type</label>
                <select 
                  value={formData.itemName} 
                  onChange={(e) => setFormData(prev => ({ ...prev, itemName: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium"
                  required
                >
                  <option value="">Select item...</option>
                  {validItemNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {isPainting && !formData.isUnique ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-left-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Width (in)</label>
                      <input 
                        type="number" 
                        value={formData.width} 
                        onChange={(e) => setFormData(p => ({ ...p, width: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Height (in)</label>
                      <input 
                        type="number" 
                        value={formData.height} 
                        onChange={(e) => setFormData(p => ({ ...p, height: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Price Per Inch (PPP)</label>
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <span className="text-gray-400 mr-2 text-xs">$</span>
                      <input 
                        type="number" 
                        step="0.01" 
                        value={formData.ppp} 
                        onChange={(e) => setFormData(p => ({ ...p, ppp: parseFloat(e.target.value) || 0 }))}
                        className="w-full bg-transparent outline-none font-bold text-blue-600"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                availableCategories.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Category</label>
                    <div className="flex flex-wrap gap-1 p-1 bg-gray-100 rounded-xl">
                      {availableCategories.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, category: cat }))}
                          className={`flex-1 min-w-[80px] py-1.5 text-xs font-bold rounded-lg transition-all ${formData.category === cat ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="relative group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                Item Photo <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="aspect-video w-full rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all overflow-hidden relative"
              >
                {formData.photoId ? (
                  <>
                    <img src={state.photos.find(p => p.id === parseInt(formData.photoId))?.url} className="w-full h-full object-cover" alt="" />
                    <button 
                      onClick={(e) => { e.stopPropagation(); setFormData(p => ({ ...p, photoId: '' })); }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md text-red-500 hover:scale-110 transition-transform"
                    >
                      <SafeIcon icon={FiXCircle} />
                    </button>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCamera} className="text-gray-400 w-8 h-8 mb-2" />
                    <span className="text-xs font-medium text-gray-500">Add Photo</span>
                  </>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Quantities & Condition</label>
              <button 
                type="button" 
                onClick={() => setFormData(prev => ({ ...prev, isUnique: !prev.isUnique }))}
                className="text-xs text-blue-600 font-bold hover:underline"
              >
                {formData.isUnique ? 'Use Standard FMV' : 'Set Custom Value'}
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {conditionConfigs.map(cond => (
                <div key={cond.key} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{cond.label}</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="number" 
                      min="0"
                      value={formData.conditions[cond.key]} 
                      onChange={(e) => setFormData(p => ({ ...p, conditions: { ...p.conditions, [cond.key]: parseInt(e.target.value) || 0 } }))}
                      className="w-full bg-transparent text-lg font-bold text-gray-700 outline-none"
                    />
                    {prices && (
                      <span className="text-[10px] text-gray-400">${prices[cond.key].toFixed(2)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {formData.isUnique && (
              <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 animate-in slide-in-from-top-2 mt-4">
                <label className="block text-xs font-bold text-purple-600 uppercase mb-2">Custom Item Price (New Value)</label>
                <div className="flex items-center bg-white rounded-lg px-3 py-2 border border-purple-200">
                  <SafeIcon icon={FiDollarSign} className="text-purple-400 mr-2" />
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.uniquePrice} 
                    onChange={(e) => setFormData(p => ({ ...p, uniquePrice: e.target.value }))}
                    className="w-full outline-none font-bold text-purple-700"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            disabled={totalQuantity === 0 || wouldExceedLimit}
            className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 transition-all shadow-lg ${totalQuantity > 0 && !wouldExceedLimit ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:scale-[1.01]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            <SafeIcon icon={state.editingItemId ? FiSave : FiPlus} />
            <span>{wouldExceedLimit ? 'Exceeds $499 Limit' : (state.editingItemId ? 'Update Item' : 'Add to Donation')}</span>
          </button>
        </form>
      );
    }

    export default ItemForm;